import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";

import {
  mail as mailApi,
} from "@/api";
import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";


const EMPTY_FORM = {
  id: "",
  field: "from",
  value: "",
  folderId: "",
  active: true,
};


function fieldLabel(value) {
  return value === "subject"
    ? "Objet"
    : "Expéditeur";
}


export default function MailRulesDialog({
  open = true,
  folders = [],
  onClose,
  embedded = false,
}) {
  const [
    rules,
    setRules,
  ] = useState([]);
  const [
    form,
    setForm,
  ] = useState(EMPTY_FORM);
  const [
    loading,
    setLoading,
  ] = useState(false);
  const [
    saving,
    setSaving,
  ] = useState(false);
  const [
    deletingId,
    setDeletingId,
  ] = useState("");

  const folderMap = useMemo(
    () => new Map(
      folders.map(
        (folder) => [
          folder.id,
          folder.name,
        ]
      )
    ),
    [folders]
  );

  async function loadRules() {
    setLoading(true);

    try {
      const response =
        await mailApi.listRules();

      setRules(
        response?.items || []
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible de charger les règles"
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(
    () => {
      if (!open) {
        setForm(EMPTY_FORM);
        return;
      }

      loadRules();
    },
    [open]
  );

  if (!open) {
    return null;
  }

  function editRule(rule) {
    setForm({
      id: rule.id,
      field: rule.field || "from",
      value: rule.value || "",
      folderId: rule.folderId || "",
      active: rule.active !== false,
    });
  }

  function resetForm() {
    setForm({
      ...EMPTY_FORM,
      folderId:
        folders[0]?.id || "",
    });
  }

  async function submit(event) {
    event.preventDefault();

    const value =
      form.value.trim();

    if (
      !value
      || !form.folderId
      || saving
    ) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        field: form.field,
        value,
        folder_id: form.folderId,
        active: Boolean(
          form.active
        ),
      };

      if (form.id) {
        await mailApi.updateRule(
          form.id,
          payload
        );

        toast.success(
          "Règle modifiée"
        );
      } else {
        await mailApi.createRule(
          payload
        );

        toast.success(
          "Règle créée"
        );
      }

      resetForm();
      await loadRules();
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible d’enregistrer la règle"
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeRule(rule) {
    if (
      !rule?.id
      || deletingId
    ) {
      return;
    }

    setDeletingId(
      rule.id
    );

    try {
      await mailApi.deleteRule(
        rule.id
      );

      if (form.id === rule.id) {
        resetForm();
      }

      await loadRules();

      toast.success(
        "Règle supprimée"
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible de supprimer la règle"
      );
    } finally {
      setDeletingId("");
    }
  }

  const content = (
    <div
      className="space-y-6"
      data-testid="mail-rules-dialog"
    >
      <div>
        <p className="text-sm leading-6 text-[var(--do-muted)]">
          Créez des règles personnelles de classement.
          MSG-045 enregistre les règles ; leur application
          automatique sera prise en charge par MSG-046.
        </p>
      </div>

      {folders.length === 0 ? (
        <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] p-4 text-sm text-[var(--do-muted)]">
          Créez d’abord un dossier personnalisé pour
          pouvoir définir une règle de classement.
        </div>
      ) : (
        <form
          className="space-y-4 rounded-2xl border border-[var(--do-border)] p-4"
          onSubmit={submit}
          data-testid="mail-rule-form"
        >
          <div className="font-black text-[var(--do-ink)]">
            {form.id
              ? "Modifier la règle"
              : "Nouvelle règle"}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className="mb-2 block text-sm font-black text-[var(--do-ink)]">
                Si
              </span>
              <select
                value={form.field}
                disabled={saving}
                onChange={(event) =>
                  setForm(
                    (current) => ({
                      ...current,
                      field:
                        event.target.value,
                    })
                  )
                }
                className="min-h-[44px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm text-[var(--do-ink)]"
                data-testid="mail-rule-field"
              >
                <option value="from">
                  Expéditeur
                </option>
                <option value="subject">
                  Objet
                </option>
              </select>
            </label>

            <label>
              <span className="mb-2 block text-sm font-black text-[var(--do-ink)]">
                Condition
              </span>
              <div className="flex min-h-[44px] items-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 text-sm font-bold text-[var(--do-ink)]">
                contient
              </div>
            </label>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-black text-[var(--do-ink)]">
              Valeur
            </span>
            <Input
              value={form.value}
              maxLength={500}
              disabled={saving}
              placeholder={
                form.field === "from"
                  ? "Ex. @client.fr"
                  : "Ex. Facture"
              }
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    value:
                      event.target.value,
                  })
                )
              }
              data-testid="mail-rule-value"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-black text-[var(--do-ink)]">
              Déplacer vers
            </span>
            <select
              value={form.folderId}
              disabled={saving}
              onChange={(event) =>
                setForm(
                  (current) => ({
                    ...current,
                    folderId:
                      event.target.value,
                  })
                )
              }
              className="min-h-[44px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm text-[var(--do-ink)]"
              data-testid="mail-rule-folder"
            >
              <option value="">
                Choisir un dossier…
              </option>
              {folders.map(
                (folder) => (
                  <option
                    key={folder.id}
                    value={folder.id}
                  >
                    {folder.name}
                  </option>
                )
              )}
            </select>
          </label>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] px-4 py-3">
            <div>
              <div className="text-sm font-black text-[var(--do-ink)]">
                Règle active
              </div>
              <div className="mt-1 text-xs text-[var(--do-muted)]">
                L’état sera utilisé par MSG-046 lors de
                l’application automatique.
              </div>
            </div>

            <Switch
              checked={Boolean(
                form.active
              )}
              disabled={saving}
              onCheckedChange={(checked) =>
                setForm(
                  (current) => ({
                    ...current,
                    active:
                      Boolean(checked),
                  })
                )
              }
              aria-label="Activer la règle"
              data-testid="mail-rule-active"
            />
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            {form.id && (
              <Button
                type="button"
                variant="actaSecondary"
                disabled={saving}
                onClick={resetForm}
              >
                Annuler la modification
              </Button>
            )}

            <Button
              type="submit"
              variant="actaPrimary"
              disabled={
                saving
                || !form.value.trim()
                || !form.folderId
              }
              data-testid="mail-rule-confirm"
            >
              {saving
                ? "Enregistrement…"
                : form.id
                  ? "Enregistrer"
                  : "Créer la règle"}
            </Button>
          </div>
        </form>
      )}

      <section>
        <div className="mb-3 text-sm font-black text-[var(--do-ink)]">
          Règles existantes
        </div>

        {loading ? (
          <div className="text-sm text-[var(--do-muted)]">
            Chargement…
          </div>
        ) : rules.length === 0 ? (
          <div className="text-sm text-[var(--do-muted)]">
            Aucune règle.
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map(
              (rule) => (
                <div
                  key={rule.id}
                  className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] p-3"
                  data-testid="mail-rule-item"
                >
                  <div className="text-sm font-black text-[var(--do-ink)]">
                    {fieldLabel(
                      rule.field
                    )} contient « {rule.value} »
                  </div>

                  <div className="mt-1 text-xs text-[var(--do-muted)]">
                    Déplacer vers{" "}
                    {folderMap.get(
                      rule.folderId
                    )
                      || rule.folderName
                      || "Dossier supprimé"}
                    {" · "}
                    {rule.active !== false
                      ? "Active"
                      : "Inactive"}
                  </div>

                  <div className="mt-3 flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="actaSecondary"
                      disabled={
                        saving
                        || Boolean(
                          deletingId
                        )
                      }
                      onClick={() =>
                        editRule(rule)
                      }
                    >
                      Modifier
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      variant="actaSecondary"
                      disabled={
                        saving
                        || Boolean(
                          deletingId
                        )
                      }
                      onClick={() =>
                        removeRule(rule)
                      }
                    >
                      {deletingId
                        === rule.id
                        ? "Suppression…"
                        : "Supprimer"}
                    </Button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </section>
    </div>

  );

  if (embedded) {
    return (
      <div data-testid="mail-rules-settings">
        {content}
      </div>
    );
  }

  return (
    <ActaModal
      open
      onClose={() => {
        if (
          !saving
          && !deletingId
        ) {
          onClose?.();
        }
      }}
      title="Règles de classement"
    >
      {content}
    </ActaModal>
  );
}
